import { inject, Injectable } from '@angular/core';
import { Indicator, xlsxColumnProperties } from '../../models/indicator';
import { TagStore } from '../../stores/tag/tag.state';
import { CategoryStore } from '../../stores/category/category.state';
import { Tag } from '../../models/tag';
import { Category } from '../../models/category';
import { validateIndicatorKeyValue } from '../../validators/key.validator';
import { IndicatorStore } from '../../stores/indicator/indicator.state';
import { MarkdownService } from '../markdown.service';
import { unitMapping } from '../../models/unit';
import { Severity } from '../../models/severity';
import { PermissionService } from '../permission.service';

export type ImportIndicator = Indicator & {
  children?: Indicator[];
  validation: {
    state: Severity;
    errors: { [key: string]: any };
    warnings: { [key: string]: any };
  };
};

@Injectable({
  providedIn: 'root',
})
export class ImportValidatorService {
  readonly tagStore = inject(TagStore);
  readonly categoryStore = inject(CategoryStore);
  readonly indicatorStore = inject(IndicatorStore);

  constructor(
    private markdownService: MarkdownService,
    private permissionService: PermissionService
  ) {}

  //indicatorSorting: { [key: string]: number } = {}; <-- leads to errors
  allImportKeys: string[] = [];
  keyArray: string[] = [];
  children: { [parent: string]: Indicator[] } = {};
  indicatorSorting: any = {};
  childSorting: any = {};
  errors: { [key: string]: any } = {};
  warnings: { [key: string]: any } = {};
  tagTitleMapping: { [index: string]: string } = {};
  categoryNameMapping: { [index: string]: string } = {};
  indicatorsToImport: ImportIndicator[] = [];
  propertyMapping = xlsxColumnProperties;
  poolImport = false;
  keyIdList: { [key: string]: string } = {};
  currentIndicators: { [key: string]: Indicator } = {};
  keys: string[] = [];

  propertiesToConvert: string[] = [
    'calculation',
    'dataCycle',
    'dataSupplier',
    'levelOfAvailability',
    'source',
    'statement',
    'hint',
    'indication',
  ];

  /**
   * Resets the service by clearing and reinitializing its internal state, including arrays, objects, and mappings used for managing data.
   *
   * @return {void} Does not return any value.
   */
  private resetService(): void {
    this.allImportKeys = [];
    this.keyArray = [];
    this.children = {};
    this.indicatorSorting = {};
    this.childSorting = {};
    this.errors = {};
    this.warnings = {};
    this.tagTitleMapping = {};
    this.categoryNameMapping = {};
    this.indicatorsToImport = [];
    this.poolImport = false;
    this.keyIdList = {};
    this.currentIndicators = {};
    this.keys = [];
  }

  /**
   * Processes and validates the uploaded data to prepare it for import. Maps the input data to structured indicators,
   * validates them, and adds any hierarchical relationships among them if present.
   *
   * @param {any} uploadData - The raw data to be validated and mapped, typically uploaded from an external source.
   * @param pool
   * @return {Array} - A collection of validated and processed indicators ready for import.
   */
  validateAndMap(uploadData: any, pool = false) {
    this.resetService();

    this.poolImport = pool;
    this.currentIndicators = pool
      ? this.indicatorStore.poolIndicators()
      : this.indicatorStore.items();

    this.keyIdList = pool
      ? this.indicatorStore.poolKeyIdList()
      : this.indicatorStore.keyIdList();
    this.keys = Object.keys(this.keyIdList);

    this.setCategoryTitleMapping();
    this.setTagTitleMapping();
    const rows = uploadData.slice().reverse();

    this.setAllKeysByRows(rows);

    rows.forEach((row: string[]) => {
      const indicator: ImportIndicator = this.indicatorFromExcelRow(row);
      const validatedIndicator = this.validateIndicator(indicator);
      this.indicatorsToImport.push(validatedIndicator);
    });

    if (Object.keys(this.children).length) {
      this.addChildrenToParent();
    }

    return this.indicatorsToImport.reverse();
  }

  /**
   * Sets the `allImportKeys` property by mapping through the provided rows and extracting the second element of each row.
   *
   * @param {any} rows - An array of rows where each row is expected to be an array. The second element of each row is used to set `allImportKeys`.
   * @return {void} - This method does not return a value.
   */
  setAllKeysByRows(rows: any): void {
    this.allImportKeys = rows.map((row: string[]) => row[1]);
  }

  /**
   * Validates the provided indicator by performing a series of checks and operations.
   *
   * @param {ImportIndicator} indicator - The indicator object to be validated.
   * @return {ImportIndicator} The result of the validation process, which includes any errors or final processed data related to the indicator.
   */
  validateIndicator(indicator: ImportIndicator): ImportIndicator {
    this.checkKeyUniqueness(indicator);
    this.validateTags(indicator);
    this.validateProducts(indicator);
    this.validateKeyPattern(indicator);
    this.assignCategory(indicator);
    if (!this.errors[indicator.key]) {
      this.addToParent(indicator);
      if (!this.keys.includes(indicator.key)) {
        this.assignSorting(indicator);
      }
      this.convertFromMarkdown(indicator);
    }
    return this.finalizeValidation(indicator);
  }

  /**
   * Checks the uniqueness of a key within a collection and updates the error state if a duplicate is found.
   * If the key is unique, it will be added to the collection.
   *
   * @param {ImportIndicator} indicator The indicator object containing the key to be checked for uniqueness.
   * @return {void} This method does not return a value.
   */
  checkKeyUniqueness(indicator: ImportIndicator): void {
    if (indicator.key && this.keyArray.includes(indicator.key)) {
      if (!this.errors[indicator.key]) this.errors[indicator.key] = {};
      this.errors[indicator.key] = { double: true };
    } else {
      this.keyArray.push(indicator.key);
    }
  }

  /**
   * Validates and processes the tags of an import indicator. It trims each tag, checks its validity
   * by comparing it with predefined mappings, and updates the indicator's tags or logs warnings if
   * a tag is invalid.
   *
   * @param {ImportIndicator} indicator -*/
  validateTags(indicator: ImportIndicator): void {
    const tags: string[] = [];
    indicator.tags?.forEach(tag => {
      tag = tag.trim();
      if (tag && this.tagTitleMapping[tag]) {
        tags.push(this.tagTitleMapping[tag]);
      } else {
        if (!this.warnings[indicator.key]) this.warnings[indicator.key] = {};
        if (!this.warnings[indicator.key]['tags'])
          this.warnings[indicator.key]['tags'] = [];
        this.warnings[indicator.key].tags.push(tag);
      }
    });
    indicator.tags = tags;
  }

  /**
   * Validates and processes the products associated with the given import indicator.
   * Updates the products list and collects warnings for any unrecognized products.
   *
   * @param {ImportIndicator} indicator - The import indicator containing a list of products to validate and map.
   * @return {void} This method does not return any value. The input indicator object is modified in place.
   */
  validateProducts(indicator: ImportIndicator): void {
    const products: string[] = [];
    indicator.products?.forEach(product => {
      if (this.tagTitleMapping[product]) {
        products.push(this.tagTitleMapping[product]);
      } else {
        if (!this.warnings[indicator.key]) this.warnings[indicator.key] = {};
        if (!this.warnings[indicator.key]['products'])
          this.warnings[indicator.key]['products'] = [];

        this.warnings[indicator.key].products.push(product);
      }
    });
    indicator.products = products;
  }

  /**
   * Assigns a category to the provided indicator based on a mapping. If the category
   * does not exist in the mapping, it logs the error for the specific indicator key.
   *
   * @param {ImportIndicator} indicator - The import indicator object containing the category information.
   * @return {void} This method does not return a value. Side effects include updating the indicator's category and potentially adding to the errors list.
   */
  assignCategory(indicator: ImportIndicator): void {
    const categoryId = indicator.category
      ? this.categoryNameMapping[indicator.category.trim()]
      : undefined;

    const hasAccess = categoryId
      ? this.permissionService.checkIndicatorRights(categoryId, 'import', false)
      : false;

    if (this.poolImport) {
      indicator.category = '';
    } else if (!categoryId) {
      if (!this.errors[indicator.key]) this.errors[indicator.key] = {};
      this.errors[indicator.key]['category'] = 'k. A.';
      indicator.category;
      this.errors[indicator.key]['category'] = indicator.category;
    } else if (!hasAccess) {
      if (!this.errors[indicator.key]) this.errors[indicator.key] = {};
      this.errors[indicator.key]['access'] = indicator.category;
    } else {
      indicator.category =
        this.categoryNameMapping[indicator.category.trim()] ||
        indicator.category;
    }
  }

  /**
   * Assigns the sorting order for the given import indicator. If the indicator has a parent,
   * it delegates the sorting assignment to a child-sorting method, otherwise it assigns sorting
   * based on category.
   *
   * @param {ImportIndicator} indicator - The import indicator for which sorting needs to be assigned.
   * @return {void} This method does not return a value.
   */
  assignSorting(indicator: ImportIndicator): void {
    if (indicator.parent) {
      this.assignChildSorting(indicator, indicator.parent);
    } else {
      this.assignCategorySorting(indicator);
    }
  }

  /**
   * Assigns sorting order to a child indicator based on its parent relationship and existing sorting configuration.
   *
   * @param {ImportIndicator} indicator - The indicator object that needs to have its sorting value set.
   * @param {string} parentKey - The key representing the parent of the indicator.
   * @return {void} This method does not return a value.
   */
  private assignChildSorting(
    indicator: ImportIndicator,
    parentKey: string
  ): void {
    const parentId: string | undefined = this.keyIdList[parentKey];

    if (this.childSorting[parentKey] !== undefined) {
      this.childSorting[parentKey]--;
    } else if (parentId) {
      this.childSorting[parentKey] = this.getInitialChildSorting(parentId) - 1;
    } else {
      this.childSorting[parentKey] = 0;
    }

    indicator.sorting = this.childSorting[parentKey];
  }

  /**
   * Retrieves the initial sorting value for a child element based on its parent's ID.
   *
   * @param {string} parentId - The unique identifier of the parent element.
   * @return {number} The sorting value of the first child element, or 1 if no child elements are found.
   */
  private getInitialChildSorting(parentId: string): number {
    const parentIndicators = this.indicatorStore.filterByParent(
      parentId,
      this.poolImport
    );
    return parentIndicators.length ? parentIndicators[0].sorting : 1;
  }

  /**
   * Assigns sorting order for a given category of the provided indicator.
   *
   * @param {ImportIndicator} indicator - The indicator object for which category sorting needs to be assigned. The object must have a `category` property representing a category and a `sorting` property for sorting value assignment.
   * @return {void} This method does not return a value but updates the `sorting` property of the passed indicator.
   */
  private assignCategorySorting(indicator: ImportIndicator): void {
    if (this.indicatorSorting[indicator.category] !== undefined) {
      this.indicatorSorting[indicator.category]--;
    } else {
      this.indicatorSorting[indicator.category] =
        this.getInitialCategorySorting(indicator.category) - 1;
    }

    indicator.sorting = this.indicatorSorting[indicator.category];
  }

  /**
   * Retrieves the initial sorting value for a given category.
   *
   * @param {string} category - The category for which the initial sorting value is to be determined.
   * @return {number} The sorting value for the first indicator in the category, or 1 if no indicators are found.
   */
  private getInitialCategorySorting(category: string): number {
    if (this.poolImport) return 1;
    const categoryIndicators = this.indicatorStore.filterByCategory(category);
    return categoryIndicators.length ? categoryIndicators[0].sorting : 1;
  }

  /**
   * Validates the key pattern of the given indicator against a set of existing keys.
   *
   * @param {ImportIndicator} indicator - The indicator object containing the key to validate.
   * @return {void} This method does not return a value, but updates the errors object if validation fails.
   */
  validateKeyPattern(indicator: ImportIndicator): void {
    const keyErrors = validateIndicatorKeyValue(
      indicator.key,
      this.keys,
      indicator.key
    );
    if (keyErrors) {
      this.errors[indicator.key] = this.errors[indicator.key] || {};
      this.errors[indicator.key].key = keyErrors;
    }
  }

  /**
   * Finalizes the validation process for the provided import indicator, determining
   * its validation state (error, warning, info, or success) based on the current
   * state of errors, warnings, or existence in the indicator store. If applicable,
   * merges the indicator with an existing one from the store.
   *
   * @param {ImportIndicator} indicator - The import indicator to validate and finalize.
   * @return {ImportIndicator} The finalized import indicator with the updated validation state.
   */
  finalizeValidation(indicator: ImportIndicator): ImportIndicator {
    // Always check if the indicator already exists in the store
    const existingIndicatorId = this.keyIdList[indicator.key];

    if (this.errors[indicator.key]) {
      // State: Error
      indicator.validation.state = 'error';
      indicator.validation.errors = this.errors[indicator.key];
    } else if (this.warnings[indicator.key]) {
      // State: Warning
      indicator.validation.state = 'warning';
      indicator.validation.warnings = this.warnings[indicator.key];
    } else if (existingIndicatorId) {
      // State: Update (merging happens here for updates without warnings/errors)
      indicator.validation.state = 'info';
    } else {
      // State: Add (new indicator without issues)
      indicator.validation.state = 'success';
    }

    // Always merge with the existing indicator if it exists
    if (existingIndicatorId) {
      indicator = this.mergeIndicatorWithImport(
        this.currentIndicators[existingIndicatorId],
        indicator
      );
    }

    return indicator;
  }

  /**
   * Merges properties between an `indicator` and an `importIndicator`, ensuring that the `importIndicator` data
   * is updated with missing or existing values from the `indicator`, and adjusts specific properties if necessary.
   *
   * @param {Indicator} indicator - The source object containing default or base property values.
   * @param {ImportIndicator} importIndicator - The target object where properties are updated or merged.
   * @return {ImportIndicator} - The updated `importIndicator` with merged properties from `indicator`.
   */
  mergeIndicatorWithImport(
    indicator: Indicator,
    importIndicator: ImportIndicator
  ): ImportIndicator {
    this.propertyMapping.forEach((property: string) => {
      importIndicator[property] =
        importIndicator[property] ?? indicator[property];
    });

    if (importIndicator.parent) {
      importIndicator.parent = this.keyIdList[importIndicator.parent];
    }

    importIndicator._id = indicator._id;
    importIndicator.sorting = indicator.sorting;

    return importIndicator;
  }

  /**
   * Maps category names to their corresponding IDs and stores the mapping.
   * Iterates over the categories in the category store, extracting the name and ID,
   * and populates the categoryNameMapping object with these values.
   *
   * @return {void} This method does not return a value.
   */
  setCategoryTitleMapping(): void {
    this.categoryStore.asArray().forEach((category: Category) => {
      this.categoryNameMapping[category.name] = category._id;
    });
  }

  /**
   * Populates the `tagTitleMapping` object with a mapping of tag titles to their corresponding IDs.
   * Iterates through all tags in the `tagStore` and assigns each tag's title as a key in the `tagTitleMapping`
   * with the corresponding tag ID as the value.
   *
   * @return {void} Does not return a value.
   */
  setTagTitleMapping(): void {
    this.tagStore.allAsArray().forEach((tag: Tag) => {
      this.tagTitleMapping[tag.title] = tag._id;
    });
  }

  /**
   * Converts an Excel row into an indicator object.
   *
   * @param {string[]} row - The array of strings representing a row from an Excel file.
   *                          Each value corresponds to a specific property of the indicator.
   * @return {ImportIndicator} The indicator object generated from the provided row,
   *                           with its properties populated based on the row values.
   */
  indicatorFromExcelRow(row: string[]): ImportIndicator {
    const indicator: ImportIndicator = {
      _id: '',
      type: 'indicator',
      categorical: false,
      category: '',
      key: '',
      name: '',
      sorting: 0,
      unit: '0',
      legend: undefined,
      pool: this.poolImport,
      validation: {
        state: 'success',
        warnings: {},
        errors: {},
      },
    };

    // Iterate over all values in a row
    row.forEach((value: string, idx: number) => {
      const property = this.propertyMapping[idx];
      const handler = this[property as keyof ImportValidatorService];
      if (typeof handler === 'function') {
        handler.call(this, indicator, value);
      } else {
        this.defaultProperty(indicator, property, value);
      }
    });

    return indicator;
  }

  /**
   * Updates the tags property of the given ImportIndicator object by splitting the provided string value.
   *
   * @param {ImportIndicator} indicator - The object whose tags property will be updated.
   * @param {string} value - A semicolon-separated string of tags to be added to the indicator's tags property.
   * @return {void}
   */
  tags(indicator: ImportIndicator, value: string): void {
    indicator['tags'] = value ? value.split(';').map(item => item.trim()) : [];
  }

  /**
   * Updates the `products` property of the given `indicator` object by splitting the `value` string
   * into an array of trimmed items, using ';' as the delimiter.
   *
   * @param {ImportIndicator} indicator - The object whose `products` property will be updated.
   * @param {string} value - The semicolon-delimited string to be split into individual product items.
   * @return {void}
   */
  products(indicator: ImportIndicator, value: string): void {
    indicator['products'] = value
      ? value.split(';').map(item => item.trim())
      : [];
  }

  /**
   * Sets the decimals property of the given indicator based on the provided value.
   *
   * @param {ImportIndicator} indicator - The indicator object to update.
   * @param {string} value - The string representation of the decimal value to be set.
   * @return {void}
   */
  decimals(indicator: ImportIndicator, value: string): void {
    indicator['decimals'] = value ? parseInt(value, 10) : undefined;
  }

  /**
   * Updates the `unit` property of the given `indicator` object based on the provided `value`.
   * If the `value` corresponds to an entry in the `unitMapping`, the mapped value is assigned to `unit`.
   * Otherwise, a default value of '1' is assigned. If the `unit` is set to '99', the `categorical` property
   * of the indicator is set to `true`.
   *
   * @param {ImportIndicator} indicator - The object whose `unit` and `categorical` properties will be updated.
   * @param {string} value - The string that determines the `unit` value. This is checked against the `unitMapping`.
   * @return {void} This method does not return a value.
   */
  unit(indicator: ImportIndicator, value: string): void {
    indicator['unit'] = value && unitMapping[value] ? unitMapping[value] : '1';
    if (indicator['unit'] === '99') indicator.categorical = true;
  }

  /**
   * Sets the lock status of the given ImportIndicator based on the provided value.
   *
   * @param {ImportIndicator} indicator - The ImportIndicator whose locked status is to be modified.
   * @param {string} value - The string representation of the lock status (1 for locked, other values for unlocked).
   * @return {void} This method does not return a value.
   */
  locked(indicator: ImportIndicator, value: string): void {
    indicator.locked = Number(value) === 1 && !this.poolImport;
  }

  /**
   * Sets a default value for a specified property on an object.
   *
   * @param {any} indicator - The target object to which the property will be assigned.
   * @param {string} property - The name of the property to set on the object.
   * @param {string} value - The value to assign to the specified property.
   * @return {void} This method does not return a value.
   */
  defaultProperty(indicator: any, property: string, value: string): void {
    indicator[property] = value;
  }

  /**
   * Updates a specified property of an indicator object with a parsed value or an empty string.
   *
   * @param {any} indicator - The object representing the indicator to be updated.
   * @param {string} property - The name of the property within the indicator object to update.
   * @param {string} value - The value to assign to the property. If not valid, it will default to an empty string.
   * @return {void}
   */
  legend(indicator: any, property: string, value: string): void {
    indicator[property] = value ? JSON.parse(value) : '';
  }

  /**
   * Converts specific properties of the given `ImportIndicator` object from Markdown to HTML format.
   *
   * @param {ImportIndicator} indicator - The object containing properties to be converted from Markdown to HTML.
   * @return {ImportIndicator} The modified `ImportIndicator` object with the specified properties converted to HTML.
   */
  private convertFromMarkdown(indicator: ImportIndicator): ImportIndicator {
    this.propertiesToConvert.forEach(property => {
      indicator[property] = indicator[property]
        ? this.markdownService.convertToHtml(indicator[property])
        : '';
    });

    return indicator;
  }

  /**
   * Adds the given indicator to its parent's list of children.
   *
   * @param {ImportIndicator} indicator - The indicator to be added. Assumes that the indicator may have a parent property specifying its parent.
   * @return {void} This method does not return a value.
   */
  private addToParent(indicator: ImportIndicator): void {
    if (indicator.parent && !this.allImportKeys.includes(indicator.parent)) {
      if (!this.warnings[indicator.key]) this.warnings[indicator.key] = {};
      this.warnings[indicator.key]['parentNotFound'] = indicator.parent;
    }
    if (indicator.parent) {
      this.children[indicator.parent]
        ? this.children[indicator.parent].push(indicator)
        : (this.children[indicator.parent] = [indicator]);
    }
  }

  /**
   * Updates the `children` property of each indicator in `indicatorsToImport`
   * by setting it to the corresponding value from `children` based on `indicator.key`,
   * if the `indicator.isSet` property is true.
   *
   * @return {void} This method does not return any value.
   */
  private addChildrenToParent(): void {
    this.indicatorsToImport.forEach(indicator => {
      if (!Number(indicator.isSet)) return;
      if (!this.children[indicator.key]) {
        this.addNoChildrenWarning(indicator);
      } else {
        indicator.children = this.children[indicator.key];
      }
    });
  }

  /**
   * Adds a warning to indicate that no children were found for the given ImportIndicator.
   *
   * @param {ImportIndicator} indicator - The ImportIndicator object to which the warning will be added.
   * @return {void} This method does not return a value.
   */
  private addNoChildrenWarning(indicator: ImportIndicator): void {
    if (!indicator.validation.warnings) indicator.validation.warnings = {};
    indicator.validation.state = 'warning';
    indicator.validation.warnings['noChildrenFound'] = true;
  }
}
